import * as THREE from 'three';
import { toCanonical, type SourceBindPose } from './retarget.ts';
import type { TakeItem } from '../io/loadFbx.ts';

/**
 * Automated "broken finger" healing. Mocap gloves drop finger tracking all the
 * time, leaving joints bent backwards or popped 90°+ for a frame. This is a
 * per-frame heuristic filter over the SOURCE skeleton's finger joints (so the
 * skeleton view, every retargeted avatar and all exports benefit):
 *
 * Detection — a joint is suspicious when its rotation-from-bind either
 *  a) exceeds anatomical limits outright (~130°+ from the bind pose), or
 *  b) deviates hard from the same joint on ADJACENT fingers while those
 *     neighbours agree with each other (the ring finger is inverted but middle
 *     and pinky match ⇒ the ring finger is the liar).
 *
 * Correction — blend the joint toward the average neighbour rotation (or the
 * bind pose when there are no usable neighbours, e.g. thumbs), with a smooth
 * confidence ramp so fingers drifting across the threshold never pop. The
 * user's "Fix broken fingers" strength (0..1) scales the whole effect.
 */

interface Joint {
  bone: THREE.Object3D;
  bindLocal: THREE.Quaternion;
  delta: THREE.Quaternion; // scratch: rotation-from-bind this frame
  healed: THREE.Quaternion; // scratch: heal target
  /**
   * Idempotence guard: the loop calls apply() every rAF even while paused (when
   * the mixer is NOT re-posing bones), so repeated slerps would compound. We
   * remember what we last wrote — if the bone still holds it, we recover the
   * raw pose and re-derive, making apply() safe at any call frequency.
   */
  lastRaw: THREE.Quaternion;
  lastHealed: THREE.Quaternion;
  hasLast: boolean;
}

interface Hand {
  actorId: string;
  side: 'left' | 'right';
  /** finger name → 3 joints (null where the rig lacks the bone). */
  fingers: Map<string, (Joint | null)[]>;
}

/** A suspicious finger joint at the CURRENT pose (report-only, no correction). */
export interface FingerAnomaly {
  actorId: string;
  side: 'left' | 'right';
  finger: string;
  /** Joint 1–3 along the finger. */
  joint: number;
  /** 0..1 — how far past the detection thresholds the joint sits. */
  confidence: number;
}

export interface FingerHealer {
  apply: (strength: number) => void;
  /** Detection only, at the current pose — powers the agent analyze() scan. */
  detect: () => FingerAnomaly[];
  /** Number of finger joints under management (0 = rig has no fingers). */
  jointCount: number;
}

const FINGERS = ['thumb', 'index', 'middle', 'ring', 'pinky'];
const NEIGHBOURS: Record<string, string[]> = {
  index: ['middle'],
  middle: ['index', 'ring'],
  ring: ['middle', 'pinky'],
  pinky: ['ring'],
};

// Thresholds (radians). DEV: deviation from neighbours that flags an outlier;
// AGREE: how closely the neighbours must match each other to be trusted;
// ABS: outright anatomical breach from the bind pose; RAMP: confidence ramp.
const DEV_T = 1.0;
const AGREE_T = 0.7;
const ABS_T = 2.3;
const THUMB_ABS_T = 2.1;
const RAMP = 0.6;

const IDENTITY = new THREE.Quaternion();
const avg = new THREE.Quaternion();
const invBind = new THREE.Quaternion();

/** Angle between two quaternions (radians, 0..π). */
function quatAngle(a: THREE.Quaternion, b: THREE.Quaternion): number {
  return 2 * Math.acos(Math.min(Math.abs(a.dot(b)), 1));
}

function clamp01(v: number): number {
  return Math.min(Math.max(v, 0), 1);
}

export function createFingerHealer(items: TakeItem[], bind: SourceBindPose): FingerHealer {
  const hands: Hand[] = [];
  let jointCount = 0;

  for (const item of items) {
    if (item.kind !== 'skeleton') continue;
    for (const side of ['left', 'right'] as const) {
      const fingers = new Map<string, (Joint | null)[]>();
      for (const finger of FINGERS) {
        const joints: (Joint | null)[] = [];
        for (let j = 1; j <= 3; j++) {
          const key = `${side}hand${finger}${j}`;
          const bone = item.bones.find((b) => toCanonical(b.name) === key) ?? null;
          const bindLocal = bone ? bind.local.get(bone) : undefined;
          if (bone && bindLocal) {
            joints.push({
              bone,
              bindLocal,
              delta: new THREE.Quaternion(),
              healed: new THREE.Quaternion(),
              lastRaw: new THREE.Quaternion(),
              lastHealed: new THREE.Quaternion(),
              hasLast: false,
            });
            jointCount++;
          } else {
            joints.push(null);
          }
        }
        if (joints.some(Boolean)) fingers.set(finger, joints);
      }
      if (fingers.size > 0) hands.push({ actorId: item.id, side, fingers });
    }
  }

  /** Deviation-from-bind for one joint, computed fresh (no memo interaction). */
  const detectDelta = new THREE.Quaternion();
  const detectInv = new THREE.Quaternion();
  const detectNeighbour = new THREE.Quaternion();

  function jointDelta(joint: Joint, out: THREE.Quaternion): THREE.Quaternion {
    detectInv.copy(joint.bindLocal).invert();
    return out.copy(detectInv).multiply(joint.bone.quaternion);
  }

  /** Report-only detection at the current pose (same thresholds as apply()). */
  function detect(): FingerAnomaly[] {
    const anomalies: FingerAnomaly[] = [];
    for (const hand of hands) {
      for (const [finger, joints] of hand.fingers) {
        for (let j = 0; j < 3; j++) {
          const joint = joints[j];
          if (!joint) continue;
          jointDelta(joint, detectDelta);
          const absAngle = quatAngle(detectDelta, IDENTITY);
          const absLimit = finger === 'thumb' ? THUMB_ABS_T : ABS_T;
          let confidence = clamp01((absAngle - absLimit) / RAMP);

          const neighbours = (NEIGHBOURS[finger] ?? [])
            .map((n) => hand.fingers.get(n)?.[j])
            .filter((n): n is Joint => !!n);
          if (neighbours.length > 0) {
            let minDeviation = Infinity;
            for (const n of neighbours) {
              jointDelta(n, detectNeighbour);
              minDeviation = Math.min(minDeviation, quatAngle(detectDelta, detectNeighbour));
            }
            let agree = true;
            if (neighbours.length >= 2) {
              jointDelta(neighbours[0], detectDelta); // reuse scratch: n0
              jointDelta(neighbours[1], detectNeighbour); // n1
              agree = quatAngle(detectDelta, detectNeighbour) < AGREE_T;
            }
            if (agree) {
              confidence = Math.max(confidence, clamp01((minDeviation - DEV_T) / RAMP));
            }
          }
          if (confidence > 0.25) {
            anomalies.push({
              actorId: hand.actorId,
              side: hand.side,
              finger,
              joint: j + 1,
              confidence: +confidence.toFixed(2),
            });
          }
        }
      }
    }
    return anomalies;
  }

  function apply(strength: number): void {
    if (strength <= 0 || jointCount === 0) return;
    for (const hand of hands) {
      // Pass 1: rotation-from-bind for every joint this frame. If the bone
      // still holds our previous heal (paused playback), recover the raw pose
      // first so healing never compounds.
      for (const joints of hand.fingers.values()) {
        for (const joint of joints) {
          if (!joint) continue;
          if (joint.hasLast && quatAngle(joint.bone.quaternion, joint.lastHealed) < 1e-4) {
            joint.bone.quaternion.copy(joint.lastRaw);
          } else {
            joint.lastRaw.copy(joint.bone.quaternion);
            joint.hasLast = true;
          }
          invBind.copy(joint.bindLocal).invert();
          joint.delta.copy(invBind).multiply(joint.bone.quaternion);
        }
      }

      // Pass 2: detect outliers and blend them toward the neighbour consensus.
      for (const [finger, joints] of hand.fingers) {
        for (let j = 0; j < 3; j++) {
          const joint = joints[j];
          if (!joint) continue;

          const absAngle = quatAngle(joint.delta, IDENTITY);
          const absLimit = finger === 'thumb' ? THUMB_ABS_T : ABS_T;
          let confidence = clamp01((absAngle - absLimit) / RAMP);

          const neighbours = (NEIGHBOURS[finger] ?? [])
            .map((n) => hand.fingers.get(n)?.[j])
            .filter((n): n is Joint => !!n);

          if (neighbours.length > 0) {
            const minDeviation = Math.min(...neighbours.map((n) => quatAngle(joint.delta, n.delta)));
            const neighboursAgree =
              neighbours.length < 2 || quatAngle(neighbours[0].delta, neighbours[1].delta) < AGREE_T;
            if (neighboursAgree) {
              confidence = Math.max(confidence, clamp01((minDeviation - DEV_T) / RAMP));
            }
            avg.copy(neighbours[0].delta);
            if (neighbours[1]) avg.slerp(neighbours[1].delta, 0.5);
            joint.healed.copy(joint.bindLocal).multiply(avg);
          } else {
            // No usable neighbours (thumb): relax toward the bind pose.
            joint.healed.copy(joint.bindLocal);
          }

          if (confidence > 0.001) {
            joint.bone.quaternion.slerp(joint.healed, confidence * strength);
          }
          joint.lastHealed.copy(joint.bone.quaternion);
        }
      }
    }
  }

  return { apply, detect, jointCount };
}
